import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import vi from "dayjs/locale/vi";
import type { ReactNode } from "react";
import type { CommentFile, CommentStaff } from "types/comment";
import { $url } from "utils/url";

dayjs.extend(relativeTime);

const imagePattern = /\.(png|jpe?g|gif|webp)$/i;

export const commentUploadUrl = `${import.meta.env.VITE_API_URL}/v1/admin/comment/upload`;

export const commentAllowExtensions = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "txt",
  "zip",
  "rar",
];

export const isCommentImage = (name: string) => imagePattern.test(name || "");

export const commentTime = (createdAt: number) => dayjs.unix(createdAt).locale(vi).fromNow();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const renderCommentContent = (content: string, mentionStaffs: CommentStaff[]) => {
  const names = [...new Set((mentionStaffs || []).map((staff) => staff.name).filter(Boolean))].sort(
    (left, right) => right.length - left.length,
  );
  if (!content) return null;
  if (!names.length) return content;

  const pattern = new RegExp(`@(${names.map(escapeRegExp).join("|")})`, "g");
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(content))) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index));
    }
    nodes.push(
      <span className="commentThread__mention" key={`${match.index}-${match[1]}`}>
        @{match[1]}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes;
};

export const CommentFiles = ({ files }: { files: CommentFile[] }) => {
  if (!files?.length) return null;

  return (
    <div className="commentThread__files">
      {files.map((file) => {
        const href = $url(file.url);
        const image = isCommentImage(file.name || file.url);
        return (
          <a
            className="commentThread__file"
            href={href}
            key={`${file.url}-${file.name}`}
            rel="noreferrer"
            target="_blank"
          >
            {image ? <img src={href} alt="" /> : null}
            <span>{file.name || file.url}</span>
          </a>
        );
      })}
    </div>
  );
};
